import { useEffect, useState } from 'react'

const DEFAULT_THRESHOLD_MS = 300

export interface UseDelayedLoaderOptions {
  /** Delay threshold in milliseconds before showing loader (default: 300ms) */
  threshold?: number
}

/**
 * Hook that provides a delayed loading indicator to avoid flash on fast operations.
 *
 * Use case: When refetching data in the background, you want to:
 * - Show existing data immediately (no flash) if fetch is fast
 * - Show a loader only if the fetch takes longer than the threshold
 *
 * @param isLoading - Whether data is currently being fetched
 * @param hasData - Whether data already exists (to distinguish initial load from background refetch)
 * @param options - Configuration options
 * @returns showLoader - Whether to show the loading indicator
 *
 * @example
 * ```tsx
 * const { response, isLoading } = useFetch(fetchData)
 * const hasData = response.length > 0
 * const showLoader = useDelayedLoader(isLoading, hasData, { threshold: 300 })
 *
 * // showLoader will be:
 * // - true immediately if isLoading && !hasData (initial load)
 * // - true after 300ms if isLoading && hasData (slow background refetch)
 * // - false if fetch completes within 300ms (fast background refetch - no flash)
 * ```
 */
const useDelayedLoader = (isLoading: boolean, hasData: boolean, options: UseDelayedLoaderOptions = {}): boolean => {
  const { threshold = DEFAULT_THRESHOLD_MS } = options
  const [showDelayedLoader, setShowDelayedLoader] = useState(false)

  useEffect(() => {
    // For background refetches (when data exists), apply delayed loader
    if (isLoading && hasData) {
      const timer = setTimeout(() => {
        setShowDelayedLoader(true)
      }, threshold)
      return () => {
        clearTimeout(timer)
      }
    }
    // Reset delayed loader when not in background refetch state
    setShowDelayedLoader(false)
    return undefined
  }, [isLoading, hasData, threshold])

  // Show loader immediately for initial load (no data), or after delay for background refetch
  return (isLoading && !hasData) || showDelayedLoader
}

export default useDelayedLoader
